<template>
  <app-drawer
    :model-value="documentsTablePane.isOpen"
    size="sm"
    :persistent="false"
    data-locator="documents-filders-pane"
    :tabs="tabs"
    v-model:tab="tab"
    @update:model-value="closePane"
    @cancel="closePane"
  >
    <template #title>{{ $t('project.documents.pane.DocumentsTablePane.drawerTitle') }}</template>

    <template #tab-share>
      <DocumentsTablePaneSelection :documents="documentsTablePane.documents" @remove="removeDocument" />
      <DocumentShare
        ref="share"
        :documents="documentsTablePane.documents"
        :force-pm-selection="true"
        @update:submit-disabled="submitDisabledByTab.share = $event"
      />
    </template>
    <template #tab-move>
      <DocumentsTablePaneSelection :documents="documentsTablePane.documents" @remove="removeDocument" />
      <!-- This component has to share its lifecycle with the pane open state -->
      <!-- because we need to fully reset its state when closing -->
      <!-- See V2-1160 for one of the things that happens when not doing that. -->
      <DocumentsPaneMove
        v-if="documentsTablePane.isOpen"
        ref="move"
        @update:submit-disabled="submitDisabledByTab.move = $event"
      />
    </template>
    <template #tab-duplicate>
      <DocumentsTablePaneSelection :documents="documentsTablePane.documents" @remove="removeDocument" />
      <DocumentsPaneDuplicate
        v-if="documentsTablePane.isOpen"
        ref="duplicate"
        @update:submit-disabled="submitDisabledByTab.duplicate = $event"
      />
    </template>

    <!-- Fixed at the bottom, as the design system requires, so only the body scrolls. Each tab
         acts on the selection its own way, so the label and the action follow the active tab
         while the form that decides the disabled state stays in the tab content. -->
    <template #footer>
      <app-button type="ghost"
                  variant="neutral"
                  data-locator="cancel-button"
                  @click="closePane"
      >
        {{ $t('common.cancel') }}
      </app-button>
      <app-button :disabled="submitDisabled"
                  :loading="submitLoading"
                  data-locator="confirm-button"
                  @click="submitActiveTab"
      >
        {{ submitText }}
      </app-button>
    </template>
  </app-drawer>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import DocumentShare from '@/common/document-share/DocumentShare.vue'
import {
  CHANGE_TABLE_DOCUMENTS_PANE_TAB,
  CLOSE_TABLE_DOCUMENTS_PANE,
  DESELECT_ALL_DOCUMENTS,
  UNSELECT_DOCUMENT,
} from '@/store/modules/documents/action_types'

import DocumentsPaneDuplicate from './DocumentsPaneDuplicate.vue'
import DocumentsPaneMove from './DocumentsPaneMove.vue'
import DocumentsTablePaneSelection from './DocumentsTablePaneSelection.vue'

export default {
  name: 'DocumentsTablePane',
  components: {
    DocumentShare,
    DocumentsPaneDuplicate,
    DocumentsPaneMove,
    DocumentsTablePaneSelection,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      // One entry per tab, because the confirm action these forms drive is now rendered outside
      // of them, and a tab that has been visited stays mounted: it reports its state on mount
      // and then only when it changes, never again when it becomes the active tab. A single
      // flag would therefore keep whichever form spoke last. Disabled until a form says
      // otherwise.
      submitDisabledByTab: {
        share: true,
        move: true,
        duplicate: true,
      },
    }
  },
  computed: {
    ...mapState('documents', [
      'documentsTablePane',
      'numberingEnabled',
      'shareLoading',
      'moveLoading',
      'duplicateLoading',
    ]),
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapGetters('user', ['currentUserId']),
    ...mapGetters('groups', ['currentUserGroupId']),
    tab: {
      get () {
        return this.documentsTablePane.tab
      },
      set (val) {
        this.CHANGE_TABLE_DOCUMENTS_PANE_TAB(val)
      },
    },
    tabs () {
      const tabs = []
      if (this.isCurrentUserPm) {
        tabs.push({
          label: this.$t('project.documents.pane.DocumentsTablePane.share'),
          value: 'share',
        })
      }
      if (this.canWriteAllFiles) {
        tabs.push({
          label: this.$t('project.documents.pane.DocumentsTablePane.move'),
          value: 'move',
        })
      }
      if (this.documentsTablePane.documents.length === 1 && this.documentsTablePane.documents[0].type === 'file') {
        tabs.push({
          label: this.$t('project.documents.pane.DocumentsTablePane.duplicate'),
          value: 'duplicate',
        })
      }
      return tabs
    },
    submitDisabled () {
      switch (this.documentsTablePane.tab) {
        case 'move':
          return this.submitDisabledByTab.move
        case 'duplicate':
          return this.submitDisabledByTab.duplicate
        default:
          return this.submitDisabledByTab.share
      }
    },
    submitText () {
      switch (this.documentsTablePane.tab) {
        case 'move':
          return this.$t('project.documents.pane.DocumentsPaneMove.move')
        case 'duplicate':
          return this.$t('project.documents.pane.DocumentsPaneDuplicate.duplicate')
        default:
          return this.$t('common.share')
      }
    },
    submitLoading () {
      switch (this.documentsTablePane.tab) {
        case 'move':
          return this.moveLoading
        case 'duplicate':
          return this.duplicateLoading
        default:
          return this.shareLoading
      }
    },
    canWriteAllFiles () {
      return this.documentsTablePane?.documents.every(doc => {
        if (doc.canWrite) {
          return true
        } else {
          return doc.rights?.write?.groups?.find((group) => group.id === this.currentUserGroupId) ||
            doc.rights?.write?.users?.find((user) => user.id === this.currentUserId)
        }
      })
    },
  },
  methods: {
    ...mapActions('documents', [
      CHANGE_TABLE_DOCUMENTS_PANE_TAB,
      CLOSE_TABLE_DOCUMENTS_PANE,
      DESELECT_ALL_DOCUMENTS,
      UNSELECT_DOCUMENT,
    ]),
    removeDocument (documentId) {
      this.UNSELECT_DOCUMENT(documentId)
    },
    // The confirm action lives in the drawer footer, outside of the scrollable body, so it
    // calls back into the form of the tab currently shown.
    submitActiveTab () {
      switch (this.documentsTablePane.tab) {
        case 'move':
          this.$refs.move?.move()
          break
        case 'duplicate':
          this.$refs.duplicate?.duplicate()
          break
        default:
          this.$refs.share?.share()
      }
    },
    closePane () {
      this.CLOSE_TABLE_DOCUMENTS_PANE()
    },
  },
}
</script>

<style lang="scss">
  .Document-name {
    max-width: 350px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .Document-chip {
    .v-chip__close:hover {
      color: rgb(var(--v-error-base)) !important;
    }
  }
</style>
